import memoizeOne from 'memoize-one';
import type { IssueId } from '@atlassian/jira-shared-types/src/general.tsx';
import type { Hash } from '@atlassian/jira-software-roadmap-model/src/common/index.tsx';
import {
	BASE_LEVEL_ITEM_HEIGHT,
	DEFAULT_ITEM_HEIGHT,
} from '@atlassian/jira-software-roadmap-timeline-table/src/common/constants.tsx';
import type { CreateItemAnchor } from '@atlassian/jira-software-roadmap-timeline-table/src/common/types/create.tsx';
import type { ExpandedItems } from '@atlassian/jira-software-roadmap-timeline-table/src/common/types/item.tsx';
import { isCreateBeforeItem, isCreateInsideItem, isRootDepth } from '../item-level.tsx';

/**
 * Calculates and returns the offset positions for each item in a timeline, optimising performance through memoization.
 *
 * @param {IssueId[]} itemIds - An array of issue IDs to calculate offsets for.
 * @param {Hash<IssueId[]>} childIdsHash - A map of parent issue IDs to their child issue IDs.
 * @param {ExpandedItems} expandedItems - Indicates which items are expanded to correctly calculate offsets.
 * @param {CreateItemAnchor} createItemAnchor - The reference point for item creation.
 *
 * @returns {Hash<number>} A map of issue IDs to their calculated offset positions.
 */
export const getItemOffsetPositions = memoizeOne(
	(
		itemIds: IssueId[],
		childIdsHash: Hash<IssueId[]>,
		expandedItems: ExpandedItems,
		createItemAnchor: CreateItemAnchor,
	): Hash<number> => {
		const positions: Hash<number> = {};
		let cumulativeOffset = 0;

		const calculateOffsetPositions = (id: IssueId, depth: number) => {
			if (isCreateBeforeItem(createItemAnchor, id)) {
				cumulativeOffset += DEFAULT_ITEM_HEIGHT;
			}

			const itemHeight = isRootDepth(depth) ? DEFAULT_ITEM_HEIGHT : BASE_LEVEL_ITEM_HEIGHT();
			positions[id] = cumulativeOffset + itemHeight / 2;

			cumulativeOffset += itemHeight;
			const childIds = childIdsHash[id];
			if (childIds && expandedItems[id]) {
				childIds.forEach((childId) => calculateOffsetPositions(childId, depth + 1));
			}

			if (isCreateInsideItem(createItemAnchor, id)) {
				cumulativeOffset += DEFAULT_ITEM_HEIGHT;
			}
		};

		itemIds.forEach((id) => calculateOffsetPositions(id, 0));
		return positions;
	},
);
